import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../utils/auth';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useTranslation } from '../../hooks/useTranslation';
import { formatKr } from '../../utils/formatKr';
import ConfirmPopup from '../../components/popups/ConfirmPopup';
import CancelReasonPopup from '../../components/popups/CancelReasonPopup';

/**
 * Account → Abonnement: subscription status, next charge, payment history
 * (GET /api/subscription/charges) and cancellation. Cancelling keeps access
 * until the paid period ends — the confirm dialog (ConfirmPopup, no native
 * window.confirm/alert) says so explicitly, but ONLY when that is true: once
 * access has lapsed, or the period end is already behind us, no retained
 * access is promised.
 *
 * The status label reflects ACCESS REALITY, not the raw Vipps agreement
 * status: an ACTIVE agreement whose renewal failed is a "Betalingsproblem"
 * while the retry ladder still grants access, and "Utløpt – betaling feilet"
 * once access has lapsed. Every input to that mapping is a server-computed
 * fact from /status (status, hasAccess, accessUntil, lastChargeFailed); the
 * paid-through policy itself is never re-derived here — see the backend's
 * lib/subscriptionAccess.js.
 *
 * "Avslutt abonnement" is two steps: an optional reason (CancelReasonPopup,
 * skippable) and then the confirm dialog, whose copy and behavior are
 * unchanged. The reason rides on the cancel request only when one was
 * given. On success a goodbye message matching the reason appears above
 * the status card; its access date is the one the confirm dialog promised
 * (retainedUntil), and it is left out when no access was promised.
 *
 * Staff (/status staffAccess, backend ADMIN_USER_IDS) with NO subscription
 * see a "Personaltilgang" note instead of the subscribe link. A staff user
 * who still has a subscription sees it exactly like anyone else, cancel
 * button included.
 */

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('nb-NO', { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Status label + tone from /status facts. Exported for the unit test.
 *
 *   active,  hasAccess, !lastChargeFailed → "Aktiv"                                   ok
 *   active,  hasAccess,  lastChargeFailed → "Betalingsproblem – tilgang til <dato>"   problem
 *   active, !hasAccess                    → "Utløpt – betaling feilet"                problem
 *   stopped, hasAccess                    → "Stoppet – tilgang til <dato>"            neutral
 *   expired, hasAccess                    → "Utløpt – tilgang til <dato>"             neutral
 *   stopped/expired, !hasAccess           → "Avsluttet"                               neutral
 *   pending/activating                    → "Venter på bekreftelse"                   neutral
 *   none/rejected/anything else           → "Ingen abonnement"                        neutral
 *
 * <dato> is accessUntil (server): periodEnd + grace for ACTIVE, periodEnd
 * for STOPPED/EXPIRED. When it is somehow missing the label falls back to the
 * bare word rather than printing "tilgang til" with nothing after it.
 */
export function subscriptionStatusView({ status, hasAccess, accessUntil, lastChargeFailed }, t) {
  const until = formatDate(accessUntil);
  const withUntil = (untilKey, bareKey) =>
    (until ? `${t(`accountSubscription.${untilKey}`)} ${until}` : t(`accountSubscription.${bareKey}`));

  switch (status) {
    case 'active':
      if (!hasAccess) return { label: t('accountSubscription.statusLapsedPaymentFailed'), tone: 'problem' };
      if (lastChargeFailed) return { label: withUntil('statusPaymentProblemUntil', 'statusPaymentProblem'), tone: 'problem' };
      return { label: t('accountSubscription.statusActive'), tone: 'ok' };
    case 'stopped':
      return hasAccess
        ? { label: withUntil('statusStoppedUntil', 'statusStopped'), tone: 'neutral' }
        : { label: t('accountSubscription.statusEnded'), tone: 'neutral' };
    case 'expired':
      return hasAccess
        ? { label: withUntil('statusExpiredUntil', 'statusExpired'), tone: 'neutral' }
        : { label: t('accountSubscription.statusEnded'), tone: 'neutral' };
    case 'pending':
    case 'activating':
      return { label: t('accountSubscription.statusPending'), tone: 'neutral' };
    default:
      return { label: t('accountSubscription.statusNone'), tone: 'neutral' };
  }
}

const GOODBYE_KEYS = {
  granted: 'goodbyeGranted',
  too_expensive: 'goodbyeTooExpensive',
  unmet_need: 'goodbyeUnmetNeed'
};

/**
 * Goodbye copy after a successful cancel. Exported for the unit test.
 * `reason` is the submitted cancelReason (other/skipped → the default copy);
 * `until` is the formatted date the confirm dialog promised, or null when it
 * promised none — then the access sentence is omitted, never invented.
 */
export function goodbyeMessage(reason, until, t) {
  const key = GOODBYE_KEYS[reason] || 'goodbyeDefault';
  const access = until ? `${t('accountSubscription.goodbyeAccessUntil')} ${until}.` : null;
  return [t(`accountSubscription.${key}Lead`), access, t(`accountSubscription.${key}Tail`)]
    .filter(Boolean)
    .join(' ');
}

const TONE_CLASS = {
  ok: 'text-success-700',
  problem: 'text-error-700',
  neutral: 'text-text-primary'
};

export default function SubscriptionSettings() {
  const { status, currentPeriodEnd, priceOre, hasAccess, accessUntil, lastChargeFailed, staffAccess, refresh } = useSubscription();
  const { t } = useTranslation();

  const [charges, setCharges] = useState(null); // null = loading
  const [chargesError, setChargesError] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(false);
  const [cancelStep, setCancelStep] = useState('reason'); // 'reason' | 'confirm'
  const [cancelAnswer, setCancelAnswer] = useState(null); // { cancelReason?, cancelReasonText? } | null
  const [goodbye, setGoodbye] = useState(null); // { reason, until } after a successful cancel

  const loadCharges = useCallback(async () => {
    setChargesError(false);
    try {
      const response = await api.get('/subscription/charges');
      const data = await response.json();
      setCharges(Array.isArray(data.charges) ? data.charges : []);
    } catch (error) {
      console.error('Error loading charges:', error);
      setCharges([]);
      setChargesError(true);
    }
  }, []);

  useEffect(() => {
    loadCharges();
  }, [loadCharges]);

  const openCancelDialog = () => {
    setCancelError(false);
    setCancelStep('reason');
    setCancelAnswer(null);
    setCancelOpen(true);
  };

  // Step 1 → step 2. answer is null when skipped or left empty.
  const proceedToConfirm = (answer) => {
    setCancelAnswer(answer);
    setCancelStep('confirm');
  };

  const closeCancelDialog = () => {
    if (!cancelling) setCancelOpen(false);
  };

  const confirmCancel = async () => {
    if (cancelling) return;
    setCancelling(true);
    setCancelError(false);
    try {
      if (cancelAnswer) {
        await api.post('/subscription/cancel', cancelAnswer);
      } else {
        await api.post('/subscription/cancel');
      }
      // retainedUntil (computed below, same render) is the date the confirm
      // dialog just promised — or null when it promised none.
      setGoodbye({ reason: cancelAnswer?.cancelReason || null, until: retainedUntil });
      setCancelOpen(false);
      await refresh();
      await loadCharges();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      // Inline in the dialog (ConfirmPopup `error`), never window.alert.
      setCancelError(true);
    } finally {
      setCancelling(false);
    }
  };

  // t() returns the key itself when a translation is missing — fall back to
  // the raw Vipps status then, so unknown charge states stay honest.
  const chargeStatusLabel = (raw) => {
    const key = `accountSubscription.chargeStatus.${raw}`;
    const label = t(key);
    return label === key ? raw : label;
  };

  const statusView = subscriptionStatusView({ status, hasAccess, accessUntil, lastChargeFailed }, t);
  const isActive = status === 'active';
  const staffWithoutSubscription = staffAccess === true && status === 'none';
  const paymentProblem = isActive && hasAccess && lastChargeFailed;
  const lapsed = isActive && !hasAccess;

  // null until GET /status has answered — the row shows a skeleton, never a
  // hardcoded fallback price (backend quotedPriceOre is the only source).
  const priceLabel = priceOre == null ? null : formatKr(priceOre);

  const periodEnd = currentPeriodEnd ? new Date(currentPeriodEnd) : null;
  const periodEndAhead = !!periodEnd && !Number.isNaN(periodEnd.getTime()) && periodEnd.getTime() > Date.now();

  // "Neste belastning" is the renewal due date = currentPeriodEnd, and it is
  // only a truthful *next* charge while that date is still ahead, the
  // agreement is healthy and the newest charge did not fail. Once the period
  // end has passed (charge processing at Vipps, a retry due on a date only
  // Vipps knows, or a lapsed ladder) there is no honest date to show, so the
  // row is omitted rather than rendering a date in the past.
  const nextChargeFormatted = isActive && hasAccess && !lastChargeFailed && periodEndAhead
    ? formatDate(periodEnd)
    : null;

  // Cancel dialog copy. The retained-access promise is only made when it is
  // true: after cancelling the doc is STOPPED, and STOPPED access ends at
  // currentPeriodEnd with NO grace (backend lib/subscriptionAccess.js) — so
  // the promised date is currentPeriodEnd (not accessUntil, which for an
  // ACTIVE doc includes the grace window and would over-promise), and only
  // while the server currently grants access AND that date is still ahead.
  // Lapsed (hasAccess false) or in grace after a failed renewal (period end
  // already passed): cancelling ends access at once, so the plain question
  // is asked with no promise attached.
  const retainedUntil = hasAccess && periodEndAhead ? formatDate(periodEnd) : null;
  const cancelMessage = retainedUntil
    ? `${t('accountSubscription.cancelConfirm')} ${t('accountSubscription.cancelConfirmUntil')} ${retainedUntil}.`
    : t('accountSubscription.cancelConfirm');

  return (
    <div className="max-w-2xl">
      <h2 className="text-xl font-semibold mb-6">{t('accountSubscription.heading')}</h2>

      {goodbye && (
        <div className="mb-6 rounded-lg border border-sage-200 bg-sage-50 p-4 text-text-primary" role="status">
          {goodbyeMessage(goodbye.reason, goodbye.until, t)}
        </div>
      )}

      <div className="space-y-8">
        {/* Status */}
        <div className="bg-white p-6 rounded-lg border">
          <h3 className="text-lg font-medium mb-4">{t('accountSubscription.statusTitle')}</h3>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-gray-500">{t('accountSubscription.statusLabel')}</span>
              <span className={`font-medium text-right ${TONE_CLASS[statusView.tone]}`}>
                {statusView.label}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-gray-500">{t('accountSubscription.priceLabel')}</span>
              {priceLabel === null ? (
                <span className="inline-block h-4 w-28 rounded bg-gray-200 animate-pulse" aria-hidden="true"></span>
              ) : (
                <span className="text-text-primary text-right">{priceLabel} {t('accountSubscription.perMonth')}</span>
              )}
            </div>
            {nextChargeFormatted && (
              <div className="flex justify-between gap-4">
                <span className="text-gray-500">{t('accountSubscription.nextCharge')}</span>
                <span className="text-text-primary text-right">{nextChargeFormatted}</span>
              </div>
            )}
          </div>

          {paymentProblem && (
            <p className="text-sm text-error-700 mt-4">{t('accountSubscription.paymentProblemNote')}</p>
          )}
          {lapsed && (
            <p className="text-sm text-error-700 mt-4">{t('accountSubscription.lapsedNote')}</p>
          )}

          <p className="text-sm text-gray-500 mt-4">{t('accountSubscription.vippsNote')}</p>

          {isActive ? (
            <div className="pt-4 mt-4 border-t">
              <button
                onClick={openCancelDialog}
                disabled={cancelling}
                className="px-4 py-2 min-h-[48px] bg-error text-white rounded-lg hover:bg-error-600 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {t('accountSubscription.cancelButton')}
              </button>
            </div>
          ) : staffWithoutSubscription ? (
            <div className="pt-4 mt-4 border-t text-sm text-text-primary">
              <p className="font-medium">{t('accountSubscription.staffAccessTitle')}</p>
              <p className="mt-1">{t('accountSubscription.staffAccessNote')}</p>
            </div>
          ) : (
            <div className="pt-4 mt-4 border-t">
              <Link
                to="/abonnement"
                className="inline-flex items-center px-4 py-2 min-h-[48px] bg-primary text-white rounded-lg hover:bg-primary-600 text-sm transition-colors"
              >
                {t('accountSubscription.subscribeLink')}
              </Link>
            </div>
          )}
        </div>

        {/* Payment history */}
        <div className="bg-white p-6 rounded-lg border">
          <h3 className="text-lg font-medium mb-4">{t('accountSubscription.historyTitle')}</h3>

          {charges === null ? (
            <p className="text-sm text-gray-500">{t('accountSubscription.historyLoading')}</p>
          ) : chargesError ? (
            <p className="text-sm text-error-700">{t('accountSubscription.historyError')}</p>
          ) : charges.length === 0 ? (
            <p className="text-sm text-gray-500">{t('accountSubscription.historyEmpty')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="pb-2 pr-4 font-medium">{t('accountSubscription.colDate')}</th>
                    <th className="pb-2 pr-4 font-medium">{t('accountSubscription.colAmount')}</th>
                    <th className="pb-2 font-medium">{t('accountSubscription.colStatus')}</th>
                  </tr>
                </thead>
                <tbody>
                  {charges.map(charge => (
                    <tr key={charge.id} className="border-b border-border-light last:border-0">
                      <td className="py-3 pr-4 text-text-primary">{formatDate(charge.due) || '–'}</td>
                      <td className="py-3 pr-4 text-text-primary">{formatKr(charge.amountOre) ?? '–'}</td>
                      <td className="py-3 text-text-primary">{chargeStatusLabel(charge.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {cancelOpen && cancelStep === 'reason' && (
        <CancelReasonPopup
          onContinue={proceedToConfirm}
          onSkip={() => proceedToConfirm(null)}
          onClose={closeCancelDialog}
        />
      )}

      {cancelOpen && cancelStep === 'confirm' && (
        <ConfirmPopup
          title={t('accountSubscription.cancelDialogTitle')}
          message={cancelMessage}
          confirmLabel={t('accountSubscription.cancelDialogConfirm')}
          cancelLabel={t('accountSubscription.cancelDialogKeep')}
          onConfirm={confirmCancel}
          onCancel={closeCancelDialog}
          busy={cancelling}
          error={cancelError ? t('accountSubscription.cancelFailed') : null}
        />
      )}
    </div>
  );
}
